import { Component, DetailEvent, Entity, EntityEventMap, registerComponent } from "aframe";
import { AnimationAction, AnimationMixer, AnimationObjectGroup, Bone, Matrix4, Object3D, SkinnedMesh, Vector3 } from "three";
import { Lazy, pathOf } from "../utils";
import { ClipAction, ClipSystem } from "./clip";


/*

"clips" component : play animations defined by "clip" components

*/


export type ClipMap = { [ filter : string ] : string[] }

interface ClipsComponent extends Component
{
	loaded : Promise<AnimationMixer>;
	mixer? : AnimationMixer;
	skeletonDimensions : { [bone:string] : number };
	data : ClipMap;
	clips : { [ filter : string ] : ClipAction[] };
}



const toggleClips = ( clips : { [rootName : string] : ClipAction[] }, rootName : string, one? : ClipAction  ) =>
{
	const actions = clips[rootName];
	if(actions)
		for(let x : ClipAction | undefined; x = actions.pop(); )
		{
			if( (x === one) || (x && one && (x.animation === one.animation)))
				continue;
			x.stop(0.1);
		}
			
		
	if(one)
		clips[rootName] = [ one ];
	else
		delete clips[rootName];
}


/* TODO : adjust position tracks to skeleton dimensions
const getSkeletonDimensions = ( obj : Object3D ) =>
{
	const result : { [name:string] : number } = {};
	const v = new Vector3();

	obj.traverse( o => {
		const sk = (o as SkinnedMesh).skeleton;
		if(!sk) return;

		for(let ix = sk.bones.length; --ix >= 0; )
		{
			const bone = sk.bones[ix];
			const name = bone.name;
			if(name in result) continue;

			let parentMatrixInverse : Matrix4 = (o as SkinnedMesh).bindMatrixInverse;
			for(let parent = bone.parent; parent; parent = parent!.parent)
			{
				const pix = sk.bones.indexOf(parent as Bone);
				if(pix < 0) continue;
				parentMatrixInverse = sk.boneInverses[pix];
				break;
			}

			/ *
			const length = v.setFromMatrixPosition( sk.boneInverses[ix] ).length();
			result[ name ] = length;
			* /
		}
	});

	return result;
};
*/


const traverseActor = (rootEl : Entity, dic : {[name:string] : Object3D}, obj : Object3D & {el? : Entity, isGroup? : boolean }) =>
{
	if(obj.el && (obj.el !== rootEl))
	{
		if(obj.el.parentEl !== rootEl)
			return;
		if(obj.isGroup)
			return;

		rootEl = obj.el;
	}
	if(obj.name)
		dic[obj.name] = obj;
	for(let child of obj.children)
		traverseActor(rootEl, dic, child);
}


function trim(value : string) { return value.trim(); }
function parseClips( value : any ) : ClipMap
{
	if( typeof value == "string")
	{
		const result : ClipMap = {};
		for(let part of value.split(";"))
		{
			const ix = part.indexOf(":");
			if(ix >= 0)
				result[ part.slice(0, ix).trim() ] = part.slice(ix+1).split(",").map(trim);
			else
				result[ "" ] = part.split(",").map(trim);
		}
		return result;
	}
	if(value && (value.constructor === Object)) //Dictionary
	{
		const result : ClipMap = {};
		for(let key in value)
		{
			const clips = value[key];
			if(Array.isArray(clips))
				result[ key ] = clips.slice();
			else
				result[ key ] = String(clips).split(",").map(trim);
		}
		return result;
	}
	return value as ClipMap;
}
function stringifyClips( value : any ) : string
{
	if(typeof value == "object")
	{
		let result = "", sep = "";
		for(let key in value)
		{
			result += sep;
			if(key) result += key + ":";
			result += ( value[key] as string[] ).join(",");
		}
		return result;
	}
	return value as string;
}



const sameList = (a : string[], b : string[])  =>
{
	if(a === b) return true;
	if(a && b && (a.length == b.length))
	{
		for(let n = a.length; --n >= 0; )
			if(a[n] != b[n])
				return false;
		return true;
	}
	return false;
};

const sameKeys = (a : any, b : any) =>
{
	if(a === b) return true;
	for(let key in a)
		if(!(key in b))
			return false;
	for(let key in b)
		if(!(key in a))
			return false;
	return true;
}


registerComponent("time", {
	schema : { type : "number", default : 0 }
});
interface TimeComponent extends Component
{
	data : number;
}



registerComponent("clips", {

	schema : {
		default : {},
		parse : parseClips,
		stringify : stringifyClips
	},
	
	init(this : ClipsComponent )
	{
		this.clips = {};
		this.skeletonDimensions = {};

		this.loaded = new Promise<AnimationMixer>( (res, rej) =>
		{
			const group = new AnimationObjectGroup();
			const mixer = new AnimationMixer(group);
			for(let key in this.el.object3DMap)
			{
				const obj = this.el.object3DMap[key] as Object3D & { isGroup ? : boolean };
				if(!(obj && obj.isGroup))
					continue;
				this.mixer = mixer;
				group.add(obj);
			}


			const mixerObjects : { [key:string] : Object3D } = {};

			this.el.addEventListener("object3dset", (event) => 
			{
				if(event.target !== this.el) return;
				const obj = event.detail.object as Object3D & { isGroup ? : boolean };
				if(!(obj && obj.isGroup))
					return;
				const oldObj = mixerObjects[ event.detail.type ];
				oldObj && group.remove(oldObj);
				group.add(obj);
				mixerObjects[ event.detail.type ] = obj;
				this.mixer || res( this.mixer = mixer );
			} );

			this.mixer && res(this.mixer);
		}  );
	},

	update(this : ClipsComponent, oldData : ClipMap)
	{
		const changedClips : ClipMap = {}, allClips : ClipMap = {}, el = this.el;

		for(let filter in this.data) //Copy
		{
			const clipList = this.data[filter], oldList = oldData && oldData[filter];
			allClips[ filter ] = clipList;

			if(sameList(oldList, clipList))
			{
				if( (filter != "") || sameKeys(this.data, oldData) )
				{
					continue;
				}
			}

			changedClips[filter] = clipList;
		}

		el.emit("reset-clips", changedClips, false);

		this.loaded.then( (mixer) => {

			const clipSystem = el.sceneEl!.systems!.clip as ClipSystem;
			if(!clipSystem) return; //uh

			const treeLoader = new Lazy< { [name:string] : Object3D }>( () => 
			{
				const result : { [name:string] : Object3D } = {};
				for(let key in el.object3DMap)
					traverseActor(el, result, el.object3DMap[key]);
				return result;
			} );

			const clipFilters : { [filter:string] : ( (obj : Object3D) => boolean ) } = {};

			// Build non-default clips filters
			for(let filter in allClips)
				if(filter)
				{
					const regex = new RegExp(filter);
					clipFilters[filter] = (obj) => regex.test(pathOf(obj, el.object3D));
				}
			
			// Build default clip filter :
			if( "" in allClips )
				clipFilters[""] = (obj) =>
				{
					for(let filter in allClips)
						if(filter && clipFilters[filter](obj))
							return false;
					return true;
				};
			

			const playClips = ( filter : string ) =>
			{
				const clipList = changedClips[filter];
				const clipFilter = clipFilters[filter];
				const trackFilter = (objectName : string, propertyName:string) => {
					const trackObj = treeLoader.get()[objectName];
					return trackObj && clipFilter(trackObj);
				};

				const playNext = () => {
					
					const nextClip = clipList[0];
					if(nextClip == "...") // Special value : linger on previous clip end
						return;

					el.emit("clip-started", nextClip, false );

					const beforeStart = (action : ClipAction) => {
						const currentList = this.data[ filter ];
						if(currentList && (currentList[0] == nextClip)) //Are we really still playing this ?
							toggleClips(this.clips, filter, action);
						else
							return false;
					};
					clipSystem.playClip(el, mixer, nextClip, trackFilter, 0.1, clipList.length < 2, beforeStart );
				};
				
				const finished = ( event : { action : AnimationAction	} ) => {
					
					const filterActions = this.clips[filter];
					if(!filterActions) return;
					if(!filterActions.some(f => f.animation === event.action)) return;
					el.emit("clip-ended", { filter, clip : clipList[0] }, false );
					clipList.shift();
					(clipList.length > 1) || mixer.removeEventListener("finished", finished as any);
					playNext();
				};

				(clipList.length > 1) && mixer.addEventListener("finished", finished as any);
				
				el.addEventListener("reset-clips", ((event : DetailEvent<ClipMap>) => {
					const newList = event.detail[filter];
					if(newList !== clipList)
						el.emit("clip-ended", { filter, clip : clipList[0] } );
					mixer.removeEventListener("finished", finished as any);
				}) as EventListener, {once:true});

				if(clipList.length) 
					playNext();
				else //Switching to non-keyframed animation
				{
					for(let action of this.clips[filter] || [])
						action.stop(0);
					delete this.clips[filter];
				}
			};
				
			for(let filter in this.clips)
				if(!(filter in allClips))
					toggleClips(this.clips, filter);
			
			for(let filter in changedClips)
				playClips(filter);
			
		} );
	},
	tick(this:ClipsComponent, t:number, dt:number)
	{
		if(this.mixer)
		{
			const time = this.el.components.time;
			if(time)
				this.mixer.setTime(time.data);
			else
				this.mixer.update(dt / 1000);
		}
	}
});